import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationCircle,
  faExclamationTriangle,
  faInfoCircle,
  faFilter
} from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../../contexts/ThemeContext';
import FindingDetailsModal from './FindingDetailsModal';
import './ScanResultsUrlTable.css';

const ScanResultsUrlTable = ({ scan, findings }) => {
  const { darkMode } = useTheme();
  const [filter, setFilter] = useState('all'); // all, critical, high, medium, low
  const [selectedUrl, setSelectedUrl] = useState(null);
  const [urlFindings, setUrlFindings] = useState([]);

  // Group findings by URL
  const groupFindingsByUrl = () => {
    if (!scan?.urlDetails || !findings) return [];

    // Create a map of URL to its findings
    const urlMap = new Map();

    scan.urlDetails.forEach((urlDetail, index) => {
      if (urlDetail.findingsCount > 0) {
        const urlFindingsList = findings.filter(f =>
          f.endpoint?.url === urlDetail.url
        );

        urlMap.set(urlDetail.url, {
          urlIndex: index,
          url: urlDetail.url,
          findingsCount: urlDetail.findingsCount,
          highestSeverity: urlDetail.highestSeverity,
          injectionPoints: urlDetail.injectionPoints,
          requestsSent: urlDetail.requestsSent,
          findings: urlFindingsList
        });
      }
    });

    return Array.from(urlMap.values());
  };

  useEffect(() => {
    setUrlFindings(groupFindingsByUrl());
  }, [scan, findings]);

  const filteredUrls = filter === 'all'
    ? urlFindings
    : urlFindings.filter(u => u.highestSeverity === filter);

  const countBySeverity = (severity) => {
    return urlFindings.filter(u => u.highestSeverity === severity).length;
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: '#dc2626',
      high: '#f97316',
      medium: '#eab308',
      low: '#3b82f6',
      informational: '#6b7280',
      none: '#6b7280'
    };
    return colors[severity] || colors.none;
  };

  const getSeverityIcon = (severity) => {
    if (severity === 'critical' || severity === 'high') {
      return <FontAwesomeIcon icon={faExclamationCircle} />;
    } else if (severity === 'medium') {
      return <FontAwesomeIcon icon={faExclamationTriangle} />;
    } else {
      return <FontAwesomeIcon icon={faInfoCircle} />;
    }
  };

  const getSeverityBadge = (severity, count) => {
    if (!count || count === 0 || severity === 'none') return null;

    return (
      <div className="severity-badge-container">
        <span
          className="severity-badge"
          style={{
            backgroundColor: getSeverityColor(severity),
            color: '#ffffff',
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '13px',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          {getSeverityIcon(severity)}
          {severity.toUpperCase()} ({count})
        </span>
      </div>
    );
  };

  const styles = {
    container: {
      marginTop: '24px'
    },
    filterBar: {
      display: 'flex',
      gap: '12px',
      marginBottom: '16px',
      padding: '16px',
      background: darkMode ? '#1f2937' : '#f9fafb',
      border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
      borderRadius: '8px'
    },
    filterButton: (active) => ({
      padding: '8px 16px',
      borderRadius: '6px',
      border: active
        ? `2px solid ${darkMode ? '#60a5fa' : '#3b82f6'}`
        : `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
      background: active
        ? (darkMode ? '#374151' : '#eff6ff')
        : (darkMode ? '#1f2937' : '#ffffff'),
      color: active
        ? (darkMode ? '#60a5fa' : '#3b82f6')
        : (darkMode ? '#d1d5db' : '#374151'),
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: active ? '600' : '500',
      transition: 'all 0.2s'
    }),
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      background: darkMode ? '#1f2937' : '#ffffff',
      border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
      borderRadius: '8px',
      overflow: 'hidden'
    },
    thead: {
      background: darkMode ? '#111827' : '#f9fafb',
      borderBottom: `2px solid ${darkMode ? '#374151' : '#e5e7eb'}`
    },
    th: {
      padding: '12px 16px',
      textAlign: 'left',
      fontSize: '12px',
      fontWeight: '600',
      textTransform: 'uppercase',
      color: darkMode ? '#9ca3af' : '#6b7280',
      whiteSpace: 'nowrap'
    },
    td: {
      padding: '12px 16px',
      borderBottom: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
      color: darkMode ? '#d1d5db' : '#374151'
    },
    row: {
      cursor: 'pointer',
      background: darkMode ? '#1f2937' : '#ffffff',
      transition: 'background-color 0.2s'
    },
    rowHover: {
      background: darkMode ? '#374151' : '#f9fafb'
    },
    emptyState: {
      padding: '48px',
      textAlign: 'center',
      color: darkMode ? '#9ca3af' : '#6b7280',
      background: darkMode ? '#1f2937' : '#f9fafb',
      border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
      borderRadius: '8px'
    }
  };

  const handleUrlClick = (urlData) => {
    setSelectedUrl(urlData);
  };

  return (
    <div style={styles.container}>
      {/* Filter Bar */}
      <div style={styles.filterBar}>
        <FontAwesomeIcon icon={faFilter} style={{ alignSelf: 'center', color: darkMode ? '#9ca3af' : '#6b7280' }} />
        <button
          onClick={() => setFilter('all')}
          style={styles.filterButton(filter === 'all')}
        >
          All ({urlFindings.length})
        </button>
        <button
          onClick={() => setFilter('critical')}
          style={styles.filterButton(filter === 'critical')}
        >
          Critical ({countBySeverity('critical')})
        </button>
        <button
          onClick={() => setFilter('high')}
          style={styles.filterButton(filter === 'high')}
        >
          High ({countBySeverity('high')})
        </button>
        <button
          onClick={() => setFilter('medium')}
          style={styles.filterButton(filter === 'medium')}
        >
          Medium ({countBySeverity('medium')})
        </button>
        <button
          onClick={() => setFilter('low')}
          style={styles.filterButton(filter === 'low')}
        >
          Low ({countBySeverity('low')})
        </button>
      </div>

      {/* URL Table */}
      {filteredUrls.length === 0 ? (
        <div style={styles.emptyState}>
          {filter === 'all'
            ? 'No findings discovered in this scan.'
            : `No ${filter} severity findings found.`}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead style={styles.thead}>
              <tr>
                <th style={styles.th}>#</th>
                <th style={styles.th}>URL</th>
                <th style={styles.th}>Highest Severity</th>
                <th style={styles.th}>Findings Count</th>
                <th style={styles.th}>Insertion Points</th>
                <th style={styles.th}>Requests Sent</th>
              </tr>
            </thead>
            <tbody>
              {filteredUrls.map((urlData, index) => (
                <tr
                  key={index}
                  style={styles.row}
                  onClick={() => handleUrlClick(urlData)}
                  onMouseEnter={(e) => e.currentTarget.style.background = styles.rowHover.background}
                  onMouseLeave={(e) => e.currentTarget.style.background = styles.row.background}
                >
                  <td style={styles.td}>
                    <span style={{ color: darkMode ? '#9ca3af' : '#6b7280', fontFamily: 'monospace' }}>
                      {urlData.urlIndex + 1}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <div style={{
                      maxWidth: '400px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontFamily: 'monospace',
                      fontSize: '13px',
                      color: darkMode ? '#d1d5db' : '#1f2937'
                    }} title={urlData.url}>
                      {urlData.url}
                    </div>
                  </td>
                  <td style={styles.td}>
                    {getSeverityBadge(urlData.highestSeverity, urlData.findingsCount)}
                  </td>
                  <td style={styles.td}>
                    <span style={{
                      fontFamily: 'monospace',
                      fontWeight: '600',
                      color: darkMode ? '#ef4444' : '#dc2626'
                    }}>
                      {urlData.findingsCount}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <span style={{
                      fontFamily: 'monospace',
                      color: darkMode ? '#60a5fa' : '#3b82f6'
                    }}>
                      {urlData.injectionPoints || 0}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <span style={{
                      fontFamily: 'monospace',
                      color: darkMode ? '#d1d5db' : '#374151'
                    }}>
                      {urlData.requestsSent || 0}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* URL Findings Modal */}
      {selectedUrl && (
        <UrlFindingsModal
          urlData={selectedUrl}
          onClose={() => setSelectedUrl(null)}
          darkMode={darkMode}
        />
      )}
    </div>
  );
};

// Modal to show all findings for a specific URL
const UrlFindingsModal = ({ urlData, onClose, darkMode }) => {
  const [selectedFinding, setSelectedFinding] = useState(null);

  const modalStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    },
    modal: {
      background: darkMode ? '#1f2937' : '#ffffff',
      borderRadius: '12px',
      maxWidth: '900px',
      width: '90%',
      maxHeight: '80vh',
      overflow: 'auto',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3)',
      border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`
    },
    header: {
      padding: '20px 24px',
      borderBottom: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      position: 'sticky',
      top: 0,
      background: darkMode ? '#1f2937' : '#ffffff',
      zIndex: 10
    },
    title: {
      margin: 0,
      color: darkMode ? '#f3f4f6' : '#1f2937',
      fontSize: '18px',
      fontWeight: '600'
    },
    url: {
      fontFamily: 'monospace',
      fontSize: '13px',
      color: darkMode ? '#9ca3af' : '#6b7280',
      marginTop: '4px',
      wordBreak: 'break-all'
    },
    closeButton: {
      background: 'transparent',
      border: 'none',
      fontSize: '24px',
      cursor: 'pointer',
      color: darkMode ? '#9ca3af' : '#6b7280',
      padding: '8px',
      borderRadius: '6px',
      transition: 'background 0.2s'
    },
    content: {
      padding: '24px'
    },
    findingCard: {
      background: darkMode ? '#111827' : '#f9fafb',
      border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '16px',
      cursor: 'pointer',
      transition: 'all 0.2s'
    }
  };

  return (
    <>
      <div style={modalStyles.overlay} onClick={onClose}>
        <div style={modalStyles.modal} onClick={(e) => e.stopPropagation()}>
          <div style={modalStyles.header}>
            <div>
              <h3 style={modalStyles.title}>
                Findings for URL ({urlData.findingsCount})
              </h3>
              <div style={modalStyles.url}>{urlData.url}</div>
            </div>
            <button style={modalStyles.closeButton} onClick={onClose}>
              ×
            </button>
          </div>
          <div style={modalStyles.content}>
            {urlData.findings.map((finding, idx) => (
              <div
                key={idx}
                style={modalStyles.findingCard}
                onClick={() => setSelectedFinding(finding)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = darkMode ? '#374151' : '#ffffff';
                  e.currentTarget.style.transform = 'translateX(4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = darkMode ? '#111827' : '#f9fafb';
                  e.currentTarget.style.transform = 'translateX(0)';
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '8px'
                }}>
                  <h4 style={{
                    margin: 0,
                    color: darkMode ? '#f3f4f6' : '#1f2937',
                    fontSize: '16px'
                  }}>
                    {finding.title}
                  </h4>
                  <span style={{
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '600',
                    backgroundColor: getSeverityColor(finding.severity),
                    color: '#ffffff'
                  }}>
                    {finding.severity.toUpperCase()}
                  </span>
                </div>
                <p style={{
                  margin: '8px 0 0 0',
                  color: darkMode ? '#9ca3af' : '#6b7280',
                  fontSize: '14px',
                  lineHeight: '1.5'
                }}>
                  {finding.description}
                </p>
                <div style={{
                  marginTop: '12px',
                  fontSize: '12px',
                  color: darkMode ? '#6b7280' : '#9ca3af'
                }}>
                  Parameter: <strong>{finding.parameter?.name || 'N/A'}</strong> |
                  Confidence: <strong>{finding.confidence}</strong>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Finding Details Modal */}
      {selectedFinding && (
        <FindingDetailsModal
          finding={selectedFinding}
          onClose={() => setSelectedFinding(null)}
        />
      )}
    </>
  );
};

// Helper function
function getSeverityColor(severity) {
  const colors = {
    critical: '#dc2626',
    high: '#f97316',
    medium: '#eab308',
    low: '#3b82f6',
    informational: '#6b7280',
    none: '#6b7280'
  };
  return colors[severity] || colors.none;
}

export default ScanResultsUrlTable;
